/**
 * Currency SSOT — single command surface for deal money display.
 *
 * Rules (product):
 * - Engine outputs are always in operating currency.
 * - outputsChf is the FX projection for reporting only.
 * - Ops UI (inputs, revenue/cost/funding detail, product, structure cards)
 *   formats with `formatOp` / `opVal`.
 * - Decision P&L Summary (and KPI strip that mirrors it) uses `formatReport` / `reportVal` (CHF).
 *
 * Do not call formatMoney/formatCurrency ad hoc for deal amounts — use this SSOT.
 */
import { formatMoney } from './formatters';
import { countryFlagEmoji } from './countryPresets';

export const REPORTING_CURRENCY = 'CHF';

/**
 * @param {{
 *   operatingCurrency?: string,
 *   countryCode?: string,
 *   fx?: object|null,
 *   outputs?: Record<string, number>,
 *   outputsChf?: Record<string, number>,
 *   taxLabel?: string,
 * }} args
 */
export function createCurrencySsot({
  operatingCurrency = 'CHF',
  countryCode = 'CH',
  fx = null,
  outputs = {},
  outputsChf = {},
  taxLabel = 'IBGI',
} = {}) {
  const op = String(operatingCurrency || 'CHF').toUpperCase();
  const report = REPORTING_CURRENCY;
  const country = String(countryCode || 'CH').toUpperCase();
  const hasChfProjection = outputsChf && typeof outputsChf === 'object'
    && Object.keys(outputsChf).length > 0;

  const opVal = (key) => {
    const v = outputs?.[key];
    return v == null || Number.isNaN(v) ? 0 : v;
  };

  /**
   * CHF reporting amount. Never silently treat operating units as CHF when FX is active.
   * @returns {number|null}
   */
  const reportVal = (key) => {
    if (outputsChf && outputsChf[key] != null && !Number.isNaN(outputsChf[key])) {
      return outputsChf[key];
    }
    // Identity: operating IS reporting.
    if (op === report) {
      return opVal(key);
    }
    return null;
  };

  return {
    /** Operating ISO code (inputs + ops sheet). */
    op,
    /** Reporting ISO code — always CHF. */
    report,
    countryCode: country,
    flag: countryFlagEmoji(country),
    taxLabel: taxLabel || 'IBGI',
    fx: fx || null,
    hasChfProjection,
    /** Raw engine amount (operating units). */
    opVal,
    /** CHF projection for reporting surfaces only (null if FX active and missing). */
    reportVal,
    formatOp: (value) => formatMoney(value, op),
    formatReport: (value) => {
      if (value == null || Number.isNaN(value)) return '—';
      return formatMoney(value, report);
    },
    /** True when ops ≠ reporting (show FX captions / dual hints). */
    isFxActive: op !== report,
  };
}

/** Surfaces allowed to read reportVal / formatReport. */
export const REPORT_SURFACES = Object.freeze(['plSummary', 'kpiHeader']);

export function assertReportSurface(surface) {
  if (!REPORT_SURFACES.includes(surface)) {
    throw new Error(`CHF reporting is only for ${REPORT_SURFACES.join(', ')}; got ${surface}`);
  }
}
